import dayjs, { Dayjs } from "dayjs"
import { useReducer, useCallback } from "react"
import { ClientDetail, ClientAddress, PhoneNumber, ClientTag } from "../../../apis/appApi"

export type PhoneEntry = {
  id?: number
  label: string
  number: string
}

export type AddressEntry = {
  id?: number
  label: string
  streetNumber: string
  streetName: string
  appartNumber: string
  city: string
  postcode: string
  state: string
  country: string
  notes: string
}

export type RecurrenceType =
  | "NONE"
  | "DAILY"
  | "XDAYS"
  | "WEEKLY"
  | "WORKDAYS"
  | "BIWEEKLY"
  | "FOURWEEKS"
  | "MONTHLY"

export type OrderCreateFormState = {
  // Customer
  lastName: string
  firstName: string
  selectedClient: { id: number; firstName: string; lastName: string; organisation?: string | null } | null
  phoneNumbers: PhoneEntry[]
  addresses: AddressEntry[]
  selectedAddressIdx: number
  customerNotes: string
  clientTags: number[]

  // Order
  workflow: "DELIVERY" | "PICKUP"
  deliveryDate: Dayjs
  recurrenceType: RecurrenceType
  dailyRecurrenceInterval: number
  recurrenceEndDate: Dayjs | null
  desiredArrivalTime: string
  desiredArrivalTimeHour: string
  desiredArrivalTimeHourEnd: string
  amount: string
  paymentMethod: string
  orderNotes: string
  employeeCode: string
  externalReference: string
  orderTags: number[]
  bags: Array<{ bagTypeId: number; number: number }>
  prescriptionImages: Array<{ data: string; filename: string; mimeType: string }>
}

export type OrderSuggestion = {
  addressId: number | null
  amount: number | null
  desiredArrivalTime: string
  notes: string | null
  tags?: Array<{ tagId: number }>
}

type Action =
  | { type: "SET_FIELD"; field: keyof OrderCreateFormState; value: any }
  | { type: "SET_LAST_NAME"; value: string }
  | { type: "SET_FIRST_NAME"; value: string }
  | { type: "SELECT_CLIENT"; client: OrderCreateFormState["selectedClient"] }
  | { type: "POPULATE_CLIENT"; detail: ClientDetail }
  | { type: "CLEAR_CLIENT" }
  | { type: "SET_PHONE"; index: number; field: keyof PhoneEntry; value: string }
  | { type: "ADD_PHONE" }
  | { type: "REMOVE_PHONE"; index: number }
  | { type: "SET_ADDRESS"; index: number; field: keyof AddressEntry; value: string }
  | { type: "ADD_ADDRESS" }
  | { type: "REMOVE_ADDRESS"; index: number }
  | { type: "SELECT_ADDRESS"; index: number }
  | { type: "TOGGLE_CLIENT_TAG"; tagId: number }
  | { type: "TOGGLE_ORDER_TAG"; tagId: number }
  | { type: "SET_WORKFLOW"; value: "DELIVERY" | "PICKUP" }
  | { type: "SET_DELIVERY_DATE"; value: Dayjs }
  | { type: "SET_RECURRENCE_TYPE"; value: RecurrenceType }
  | { type: "SET_DAILY_RECURRENCE_INTERVAL"; value: number }
  | { type: "SET_RECURRENCE_END_DATE"; value: Dayjs | null }
  | { type: "SET_DESIRED_ARRIVAL_TIME"; value: string }
  | { type: "SET_DESIRED_ARRIVAL_TIME_HOUR"; value: string }
  | { type: "SET_DESIRED_ARRIVAL_TIME_HOUR_END"; value: string }
  | { type: "SET_AMOUNT"; value: string }
  | { type: "SET_PAYMENT_METHOD"; value: string }
  | { type: "SET_ORDER_NOTES"; value: string }
  | { type: "SET_EMPLOYEE_CODE"; value: string }
  | { type: "SET_EXTERNAL_REFERENCE"; value: string }
  | { type: "SET_CUSTOMER_NOTES"; value: string }
  | { type: "SET_BAGS"; bags: Array<{ bagTypeId: number; number: number }> }
  | { type: "ADD_PRESCRIPTION_IMAGE"; image: { data: string; filename: string; mimeType: string } }
  | { type: "REMOVE_PRESCRIPTION_IMAGE"; index: number }
  | { type: "FILL_FROM_SUGGESTION"; suggestion: OrderSuggestion }
  | { type: "RESET" }

const emptyPhone: PhoneEntry = { label: "Cell", number: "" }
const emptyAddress: AddressEntry = {
  label: "",
  streetNumber: "",
  streetName: "",
  appartNumber: "",
  city: "",
  postcode: "",
  state: "",
  country: "",
  notes: "",
}

export function getInitialState(): OrderCreateFormState {
  return {
    lastName: "",
    firstName: "",
    selectedClient: null,
    phoneNumbers: [{ ...emptyPhone }],
    addresses: [{ ...emptyAddress }],
    selectedAddressIdx: 0,
    customerNotes: "",
    clientTags: [],
    workflow: "DELIVERY",
    deliveryDate: dayjs(),
    recurrenceType: "NONE",
    dailyRecurrenceInterval: 2,
    recurrenceEndDate: null,
    desiredArrivalTime: "MORNING",
    desiredArrivalTimeHour: "",
    desiredArrivalTimeHourEnd: "",
    amount: "",
    paymentMethod: "NONE",
    orderNotes: "",
    employeeCode: "",
    externalReference: "",
    orderTags: [],
    bags: [],
    prescriptionImages: [],
  }
}

function mapPhoneNumbers(phones: PhoneNumber[]): PhoneEntry[] {
  if (phones.length === 0) return [{ ...emptyPhone }]
  return phones.map((p) => ({
    id: p.id,
    label: p.label || "Cell",
    number: p.number,
  }))
}

function mapAddresses(clientAddresses: ClientAddress[]): AddressEntry[] {
  if (clientAddresses.length === 0) return [{ ...emptyAddress }]
  return clientAddresses.map((ca) => ({
    id: ca.address?.id,
    label: ca.address?.label || "",
    streetNumber: ca.address?.streetNumber || "",
    streetName: ca.address?.streetName || "",
    appartNumber: ca.address?.appartNumber || "",
    city: ca.address?.city || "",
    postcode: ca.address?.postcode || "",
    state: ca.address?.state || "",
    country: ca.address?.country || "",
    notes: ca.address?.notes || "",
  }))
}

function mapClientTags(tags: ClientTag[]): number[] {
  return tags.map((ct) => ct.tagId)
}

function reducer(state: OrderCreateFormState, action: Action): OrderCreateFormState {
  switch (action.type) {
    case "SET_LAST_NAME":
      return { ...state, lastName: action.value }
    case "SET_FIRST_NAME":
      return { ...state, firstName: action.value }
    case "SELECT_CLIENT":
      return {
        ...state,
        selectedClient: action.client,
        lastName: action.client?.lastName ?? "",
        firstName: action.client?.firstName ?? "",
      }
    case "POPULATE_CLIENT": {
      const d = action.detail
      return {
        ...state,
        phoneNumbers: mapPhoneNumbers(d.phoneNumbers),
        addresses: mapAddresses(d.addresses),
        selectedAddressIdx: 0,
        customerNotes: d.notes || "",
        clientTags: mapClientTags(d.tags),
      }
    }
    case "CLEAR_CLIENT":
      return {
        ...state,
        selectedClient: null,
        lastName: "",
        firstName: "",
        phoneNumbers: [{ ...emptyPhone }],
        addresses: [{ ...emptyAddress }],
        selectedAddressIdx: 0,
        customerNotes: "",
        clientTags: [],
      }
    case "SET_PHONE": {
      const phones = [...state.phoneNumbers]
      phones[action.index] = { ...phones[action.index], [action.field]: action.value }
      return { ...state, phoneNumbers: phones }
    }
    case "ADD_PHONE":
      return { ...state, phoneNumbers: [...state.phoneNumbers, { ...emptyPhone }] }
    case "REMOVE_PHONE": {
      const phones = state.phoneNumbers.filter((_, i) => i !== action.index)
      return { ...state, phoneNumbers: phones.length === 0 ? [{ ...emptyPhone }] : phones }
    }
    case "SET_ADDRESS": {
      const addrs = [...state.addresses]
      addrs[action.index] = { ...addrs[action.index], [action.field]: action.value }
      return { ...state, addresses: addrs }
    }
    case "ADD_ADDRESS":
      return { ...state, addresses: [...state.addresses, { ...emptyAddress }] }
    case "REMOVE_ADDRESS": {
      const addrs = state.addresses.filter((_, i) => i !== action.index)
      const newIdx = state.selectedAddressIdx >= addrs.length ? 0 : state.selectedAddressIdx
      return { ...state, addresses: addrs.length === 0 ? [{ ...emptyAddress }] : addrs, selectedAddressIdx: newIdx }
    }
    case "SELECT_ADDRESS":
      return { ...state, selectedAddressIdx: action.index }
    case "TOGGLE_CLIENT_TAG": {
      const tags = state.clientTags.includes(action.tagId)
        ? state.clientTags.filter((id) => id !== action.tagId)
        : [...state.clientTags, action.tagId]
      return { ...state, clientTags: tags }
    }
    case "TOGGLE_ORDER_TAG": {
      const tags = state.orderTags.includes(action.tagId)
        ? state.orderTags.filter((id) => id !== action.tagId)
        : [...state.orderTags, action.tagId]
      return { ...state, orderTags: tags }
    }
    case "SET_WORKFLOW":
      return { ...state, workflow: action.value }
    case "SET_DELIVERY_DATE":
      return { ...state, deliveryDate: action.value }
    case "SET_RECURRENCE_TYPE":
      return {
        ...state,
        recurrenceType: action.value,
        dailyRecurrenceInterval:
          action.value === "XDAYS" ? state.dailyRecurrenceInterval : 2,
      }
    case "SET_DAILY_RECURRENCE_INTERVAL":
      return { ...state, dailyRecurrenceInterval: action.value }
    case "SET_RECURRENCE_END_DATE":
      return { ...state, recurrenceEndDate: action.value }
    case "SET_DESIRED_ARRIVAL_TIME":
      return {
        ...state,
        desiredArrivalTime: action.value,
        desiredArrivalTimeHour:
          ["BEFORE", "AFTER", "BETWEEN"].includes(action.value)
            ? state.desiredArrivalTimeHour
            : "",
        desiredArrivalTimeHourEnd:
          action.value === "BETWEEN" ? state.desiredArrivalTimeHourEnd : "",
      }
    case "SET_DESIRED_ARRIVAL_TIME_HOUR":
      return { ...state, desiredArrivalTimeHour: action.value }
    case "SET_DESIRED_ARRIVAL_TIME_HOUR_END":
      return { ...state, desiredArrivalTimeHourEnd: action.value }
    case "SET_AMOUNT":
      return { ...state, amount: action.value }
    case "SET_PAYMENT_METHOD":
      return { ...state, paymentMethod: action.value }
    case "SET_ORDER_NOTES":
      return { ...state, orderNotes: action.value }
    case "SET_EMPLOYEE_CODE":
      return { ...state, employeeCode: action.value }
    case "SET_EXTERNAL_REFERENCE":
      return { ...state, externalReference: action.value }
    case "SET_CUSTOMER_NOTES":
      return { ...state, customerNotes: action.value }
    case "SET_BAGS":
      return { ...state, bags: action.bags }
    case "ADD_PRESCRIPTION_IMAGE":
      return {
        ...state,
        prescriptionImages: [...state.prescriptionImages, action.image],
      }
    case "REMOVE_PRESCRIPTION_IMAGE":
      return {
        ...state,
        prescriptionImages: state.prescriptionImages.filter(
          (_, i) => i !== action.index,
        ),
      }
    case "FILL_FROM_SUGGESTION": {
      const s = action.suggestion
      // Find matching address index by addressId
      const addrIdx = s.addressId != null
        ? state.addresses.findIndex((a) => a.id === s.addressId)
        : -1
      // Convert amount from cents to display string
      const amountDisplay = s.amount != null && s.amount > 0
        ? `${Math.floor(s.amount / 100)}.${String(s.amount % 100).padStart(2, "0")}`
        : ""
      // Collect tag IDs from suggestion
      const tagIds = (s.tags ?? []).map((t) => t.tagId)
      return {
        ...state,
        ...(addrIdx >= 0 ? { selectedAddressIdx: addrIdx } : {}),
        amount: amountDisplay,
        desiredArrivalTime: s.desiredArrivalTime || state.desiredArrivalTime,
        orderNotes: s.notes || state.orderNotes,
        ...(tagIds.length > 0 ? { orderTags: tagIds } : {}),
      }
    }
    case "SET_FIELD":
      return { ...state, [action.field]: action.value }
    case "RESET":
      return getInitialState()
    default:
      return state
  }
}

export function useOrderCreateForm() {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState)

  const reset = useCallback(() => dispatch({ type: "RESET" }), [])

  return { state, dispatch, reset }
}
